import React from 'react';
import { ChevronDown, CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface CardProps {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  errors?: string[];
  action?: React.ReactNode;
  /** Stable selector for tests and screenshots, on the card root. */
  testId?: string;
}

/**
 * Reusable card component for the product editor layout
 */
export const Card: React.FC<CardProps> = ({
  title,
  children,
  collapsible = false,
  defaultExpanded = true,
  errors = [],
  action,
  testId,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  const hasErrors = errors.length > 0;

  return (
    <Collapsible
      open={!collapsible || expanded}
      onOpenChange={collapsible ? setExpanded : undefined}
      data-testid={testId}
      className={cn(
        'rounded-xl border bg-card transition-colors',
        hasErrors && 'border-destructive/50'
      )}
    >
      {/* Card Header — section label style */}
      <div
        onClick={collapsible ? () => setExpanded(!expanded) : undefined}
        className={cn(
          'flex items-center justify-between px-6 pt-5 pb-3',
          collapsible && 'cursor-pointer'
        )}
      >
        <div className="flex items-center gap-3">
          <p className="text-xs font-medium tracking-[0.04em] text-muted-foreground uppercase">
            {title}
          </p>

          {/* Error indicator */}
          {hasErrors && (
            <div className="flex items-center gap-1 bg-destructive/10 px-2 py-0.5">
              <CircleAlert className="size-3 text-destructive" />
              <span className="text-xs text-destructive">
                {errors.length}{' '}
                {errors.length === 1
                  ? t('PAGES.NEW_PRODUCT.V3.CARD.ERROR')
                  : t('PAGES.NEW_PRODUCT.V3.CARD.ERRORS')}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {action}

          {collapsible && (
            <ChevronDown
              className={cn(
                'size-4 text-muted-foreground transition-transform',
                expanded && 'rotate-180'
              )}
            />
          )}
        </div>
      </div>

      {/* Card Content */}
      <CollapsibleContent>
        <div className="px-6 pt-2 pb-6">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};
